// -------------------------------
// 🔹 Imports externes (librairies tierces)
// -------------------------------
import { useMutation } from '@tanstack/vue-query';
import type { AxiosError } from 'axios';

// -------------------------------
// 🔹 Imports internes — Références réactives
// -------------------------------
import { isGlobalError, GlobalErrorMessage } from '@/refs/common.refs';
import { isAtcivatedWatchMangeOraganisation } from '@/refs/manageOrgCommerce.ref';

// -------------------------------
// 🔹 Imports internes — Store
// -------------------------------
import { useOrgContactStore } from '@/store/orgContact.store';

// -------------------------------
// 🔹 Imports internes — Types
// -------------------------------
import type {
  ICreateOrgContactBody,
  IUpdateOrgContactBody,
} from '@/types/manageOrgCommerce.types';

/**
 * Hook pour gérer les mutations liées aux contacts d'une organisation.
 */
export const OrgContactMutation = () => {
  const orgContactStore = useOrgContactStore();

  // -------------------------------
  // 🔹 Gestion des erreurs globale
  // -------------------------------
  const handleError = (err: any) => {
    const error = err as AxiosError;
    const response = error.response?.data as any;
    const message = response?.message?.[0];

    isGlobalError.value = true;
    GlobalErrorMessage.value = message;
    isAtcivatedWatchMangeOraganisation.value =
      !isAtcivatedWatchMangeOraganisation.value;
    setTimeout(() => {
      isGlobalError.value = false;
    }, 1000);
  };

  // -------------------------------
  // 🔹 Création d'un contact
  // -------------------------------
  const createOrgContact = useMutation<
    any,
    Error,
    { organizationId: string; data: ICreateOrgContactBody }
  >({
    mutationFn: ({ organizationId, data }) =>
      orgContactStore.createOrgContact(organizationId, data),
    onSuccess: () => {},
    onError: (err) => handleError(err),
  });

  // -------------------------------
  // 🔹 Mise à jour d'un contact
  // -------------------------------
  const updateOrgContact = useMutation<
    any,
    Error,
    { organizationId: string; contactId: string; data: IUpdateOrgContactBody }
  >({
    mutationFn: ({ organizationId, contactId, data }) =>
      orgContactStore.updateOrgContact(organizationId, contactId, data),
    onSuccess: () => {},
    onError: (err) => handleError(err),
  });

  // -------------------------------
  // 🔹 Suppression d'un contact
  // -------------------------------
  const deleteOrgContact = useMutation<
    any,
    Error,
    { organizationId: string; contactId: string }
  >({
    mutationFn: ({ organizationId, contactId }) =>
      orgContactStore.deleteOrgContact(organizationId, contactId),
    onSuccess: () => {},
    onError: (err) => handleError(err),
  });

  // -------------------------------
  // 🔹 Fonctions exposées pour les composants
  // -------------------------------
  const handleCreateOrgContact = (
    organizationId: string,
    data: ICreateOrgContactBody,
  ) => createOrgContact.mutateAsync({ organizationId, data });

  const handleUpdateOrgContact = (
    organizationId: string,
    contactId: string,
    data: IUpdateOrgContactBody,
  ) => updateOrgContact.mutateAsync({ organizationId, contactId, data });

  const handleDeleteOrgContact = (organizationId: string, contactId: string) =>
    deleteOrgContact.mutateAsync({ organizationId, contactId });

  return {
    // mutations
    createOrgContact,
    updateOrgContact,
    deleteOrgContact,
    // handlers
    handleCreateOrgContact,
    handleUpdateOrgContact,
    handleDeleteOrgContact,
  };
};
